import React from 'react';
import { Link } from "react-router-dom";
import Highlight from 'src/components/Highlight';
import routes from 'src/routes';
import url from 'src/util/url';
import Page from 'src/components/Page';
import ExpandDoc from 'src/components/ExpandDoc';

function TxnApplePayPage() {
    return <Page header="Apple Pay">
        <p>
            Apple Pay is an easy and secure payment method for Apple users. With Apple Pay, your customers can check out from your online store or app by using Safari on the iPhone, iPad, and Mac.
        </p>
        <h3>
            How to enable Apple Pay on the BPOINT Hosted Payment Page
        </h3>
        <p>
            To enable Apple Pay on your BPOINT Hosted Payment Page, please contact your Relationship Manager or our Merchant Support Helpdesk on 1800 230 177 (option 1, 3, 1) Monday to Friday 9am to 5pm (AEST). 
        </p>
        <h3>
            How to enable Apple Pay on the BPOINT API
        </h3>
        <p>
            Please follow the instructions below to update your BPOINT API, you will also need to register with Apple Pay and configure their environment. 
            See <a href='https://developer.apple.com/documentation/applepaywebmerchantregistrationapi/registering_with_apple_pay_and_applying_to_use_the_api'>Apple Pay</a> for more information on how to register with Apple Pay. 
        </p>
        <p>
            If you have any questions about the process or require any support with the BPOINT API, please contact your Relationship Manager or our Merchant Support Helpdesk on 1800 230 177 (option 1, 3, 1) Monday to Friday 9am to 5pm (AEST). 
        </p>
        <h3>1. <Link to={routes.api.spec + "#operation/TxnAuthkey_CreateAuthkey"}>Create txn authkey</Link></h3>
        <h3>2. <Link to={routes.api.spec + "#operation/TxnAuthkey_AttachTxnDetails"}>Attach txn details</Link></h3>
        <h3>3. <a href={'https://developer.apple.com/documentation/apple_pay_on_the_web/apple_pay_js_api'}>Integrate with Apple Pay JS API</a></h3>
        <ol>
            <li>An <a href='https://applepaydemo.apple.com/#display-button'>Apple Pay button</a> should be available for the customer.</li>
            <li>When the Apple Pay button is pressed, <code>ApplePaySession</code> needs to be constructed to track the transaction and handle callbacks. The interface 
            that needs to be implemented for <code>ApplePaySession</code> can be found <a href='https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/'>here</a>.</li>
            <li>Before the payment sheet can be displayed, the <a href="https://applepaydemo.apple.com/#merchantValidation">merchant validation process</a> needs to be completed.
            This process begins by calling <code>begin</code> on <code>ApplePaySession</code>. The <code>onvalidatemerchant</code> handler will be then called, expecting a request to be sent to the merchant's server in order to get a merchant session.
            On behalf of the browser, the server must <a href="https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778021-onvalidatemerchant">communicate with Apple to get a merchant session </a>
            object and send it back as a response to the original request. Once the browser receives the merchant session, it must call <code>completeMerchantValidation</code> on the
            <code>ApplePaySession</code> to complete merchant validation.
            </li>
            <li>Once the merchant session has been successfully added to the user's browser, the payment sheet will be displayed on the browser.</li>
            <li>Once the customer confirms the transaction via the payment sheet, <code>onpaymentauthorized</code> handler in <code>ApplePaySession</code> will be called with
            an <code>ApplePayPayment</code> object. The <code>ApplePayPayment</code> will contain an object called <code>ApplePayPaymentToken</code>.</li>
            <li>This <code>ApplePayPaymentToken</code> needs to be passed to <code>BPOINT.txn.authkey.attachApplePayPaymentToken</code> that will faciliate payment processing as mentioned <a href='https://applepaydemo.apple.com/#authorizingThePayment'>here</a>.</li>
        </ol>
        <h3>4. <Link to={routes.api.spec + "#operation/TxnAuthkey_Process"}>Process</Link></h3>
        <h3>5. <a href="https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778012-completepayment">Call completePayment</a> within <code>onpaymentauthorized</code></h3>

        <h2>JavaScript</h2>
        <p>To enable JavaScript integration, you must include the following script tag in your page.</p>
        <Highlight language={'html'}>
            {`<script src="${url.api("clientscripts/api.js")}" type="text/javascript"></script>`}
        </Highlight>

        <h2 id="Specification">Specification</h2>

        <ExpandDoc
            method="BPOINT.txn.authkey.attachApplePayPaymentToken"
            doc={{
                authkey: {
                    type: 'string',
                    description: "Transaction authkey to attach payment method to"
                },
                applePayPaymentToken: {
                    type: 'ApplePayPaymentToken',
                    description: <>See request body of <Link to={routes.api.spec + "#operation/TxnAuthkeyClient_AttachApplePayPaymentToken"}>TxnAuthkeyClient - AttachApplePayPaymentToken</Link>, <a href="https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymenttoken">Apple Pay API type</a></>
                },
                callback: {
                    type: "function",
                    description: "A callback function that is executed when the submission result is received",
                    children: {
                        code: {
                            type: "string",
                            description: <>One of <code>success</code>, <code>error</code>, or <code>timeout</code></>
                        },
                        data: {
                            type: "object",
                            description: <>See response of <Link to={routes.api.spec + "#operation/TxnAuthkeyClient_AttachApplePayPaymentToken"}>TxnAuthkeyClient - AttachApplePayPaymentToken</Link></>
                        }
                    }
                }
            }}
        />
    </Page>
}
export default TxnApplePayPage;